<template>
  <div class="container--fluid d-flex flex-column gap-6 mx-4">
    <template v-if="!showAppointmentFields && !selectedFeatureTriggerId">
      <v-row class="mt-4">
        <v-col lg="8" md="12" class="pb-0">
          <div class="d-flex flow-column justify-space-between">
            <div>
              <h3>{{ $t('warehouse.pages.fieldsAndForms.headers.appointmentCreation') }}</h3>
              <p>
                {{ $t('customForms.messages.manageTheFormRequiredForAppointmentCreation') }}
              </p>
            </div>
            <v-spacer />
            <div class="font-size-small">
              <preview-as-carrier-button :warehouse="warehouse" class="mt-4" />
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mb-4 mt-0">
        <v-col lg="8" md="12">
          <v-expansion-panels variant="accordion">
            <v-expansion-panel
              readonly
              @click="showAppointmentFields = true"
              data-testid="custom-fields">
              <v-expansion-panel-title>
                <h4>{{ $t('warehouse.pages.fieldsAndForms.headers.allAppointments') }}</h4>
                <template v-slot:actions>
                  <v-btn
                    size="small"
                    variant="flat"
                    base-color="disabled"
                    data-testid="appointment-fields">
                    {{ $t('customForms.buttons.manage') }}
                    <v-icon size="small" class="ml-2">mdi-arrow-right</v-icon>
                  </v-btn>
                </template>
              </v-expansion-panel-title>
            </v-expansion-panel>

            <v-expansion-panel data-testid="loadtype-forms" key="loadtype-forms">
              <v-expansion-panel-title>
                <h4>{{ CustomFormsFeaturesData[CustomFormsFeatures.BookLoadType].title }}</h4>
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <custom-forms-feature-load-type
                  :warehouse="warehouse"
                  :triggers="loadtypeTriggers"
                  @form-created="handleFormCreated"
                  @manage="manageFeatureTrigger" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <div>
        <h3>{{ $t('customForms.headers.gateYard') }}</h3>
        <p>{{ $t('customForms.messages.manageTheFormsRequiredForArrivalsAndDepartures') }}</p>
      </div>

      <v-row class="mb-4">
        <v-col lg="8" md="12">
          <v-expansion-panels>
            <v-expansion-panel v-for="[_, feature] of gateManagementFeatures" :key="feature">
              <v-expansion-panel-title>
                <h4>
                  {{ featureData(feature).title || 'Unknown' }}
                  <v-chip
                    v-if="feature === CustomFormsFeatures.CheckInAdditionalAssetDetails"
                    class="ml-2"
                    color="disabled"
                    size="x-small">
                    {{ $t('warehouse.pages.fieldsAndForms.chips.addOnFeature').toUpperCase() }}
                  </v-chip>
                  <v-chip
                    class="ml-2 bg-primary typography-body-sm-bold text-uppercase"
                    density="compact"
                    v-if="featureData(feature).showNewTag">
                    <small>{{ $t('global.basicTerms.new') }}</small>
                  </v-chip>
                </h4>
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <custom-forms-feature
                  :feature="feature"
                  :triggers="featureTriggers(feature)"
                  @manage="manageFeatureTrigger" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <div>
        <h3>{{ $t('customForms.headers.claimsSupport') }}</h3>
        <p>
          {{ $t('customForms.messages.manageFormsRequiredForDamageAndOtherClaimDisputes') }}
        </p>
      </div>

      <v-row>
        <v-col lg="8" md="12">
          <v-expansion-panels>
            <v-expansion-panel v-for="[_, feature] of claimSupportFeatures" :key="feature">
              <v-expansion-panel-title>
                <h4>
                  {{ featureData(feature).title || 'Unknown' }}
                </h4>
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <custom-forms-feature
                  :feature="feature"
                  :triggers="featureTriggers(feature)"
                  @manage="manageFeatureTrigger" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </template>

    <template v-if="showAppointmentFields">
      <appointment-fields :warehouse="warehouse" @back="() => (showAppointmentFields = false)" />
    </template>

    <template v-if="selectedFeatureTrigger?.id">
      <custom-forms-trigger
        :warehouse="warehouse"
        :trigger="selectedFeatureTrigger"
        :related-triggers="relatedTriggers"
        @data-updated="getAllTriggers"
        @back="() => (selectedFeatureTriggerId = null)" />
    </template>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useEventHub, useSockets } from '@/composables';
import {
  CustomFormsClaimFeatures,
  CustomFormsGateManagementFeatures,
  CustomFormsFeatures,
  CustomFormFlowEntitiesEnum,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum,
  getTranslatedCustomFormsFeaturesData
} from '@nova/core';
import CustomFormsFeatureLoadType from '../custom_forms/CustomFormsFeatureLoadType.vue';
import CustomFormsFeature from '../custom_forms/CustomFormsFeature.vue';
import CustomFormsTrigger from '../custom_forms/CustomFormsTrigger.vue';
import AppointmentFields from '../AppointmentFields.vue';
import customFormsService from '@satellite/services/custom-forms-service';

export default defineComponent({
  props: {
    warehouse: {
      type: Object,
      required: true
    }
  },
  components: {
    CustomFormsFeatureLoadType,
    CustomFormsFeature,
    CustomFormsTrigger,
    AppointmentFields
  },
  setup(props) {
    const showAppointmentFields = ref(false);

    const selectedFeatureTriggerId = ref(null);

    const warehouseTriggers = ref([]);
    const loadtypeTriggers = ref([]);
    const warehouseLoadTypes = ref([]);

    const sockets = useSockets();
    const eventHub = useEventHub();

    const CustomFormsFeaturesData = computed(() => getTranslatedCustomFormsFeaturesData());

    const selectedFeatureTrigger = computed(() =>
      [...warehouseTriggers.value, ...loadtypeTriggers.value].find(
        t => t.id === selectedFeatureTriggerId.value
      )
    );

    const relatedTriggers = computed(() => {
      const triggers = [...warehouseTriggers.value, ...loadtypeTriggers.value].filter(
        t =>
          Boolean(t.flow?.parentCode) &&
          t.flow.parentCode === selectedFeatureTrigger.value?.flow?.parentCode
      );

      triggers.forEach(trigger => {
        if (trigger.entityName === CustomFormFlowEntitiesEnum.loadtype) {
          trigger.loadType = warehouseLoadTypes.value.find(l => l.id === trigger.objectId);
        }
      });

      return triggers;
    });

    const claimSupportFeatures = computed(() =>
      Object.entries(CustomFormsFeatures)
        // eslint-disable-next-line no-unused-vars
        .filter(([_, value]) => CustomFormsClaimFeatures.includes(value))
        .sort()
    );

    const gateManagementFeatures = computed(() =>
      Object.entries(CustomFormsFeatures)
        // eslint-disable-next-line no-unused-vars
        .filter(([_, value]) => {
          // When GM is disabled, show only CheckOut
          if (!isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.GateManagement)) {
            return value === CustomFormsFeatures.CheckOut;
          }

          return (
            CustomFormsGateManagementFeatures.includes(value) &&
            value !== CustomFormsFeatures.CheckIn
          );
        })
        .sort()
    );

    const getWarehouseTriggers = async () => {
      if (!props.warehouse?.id) {
        return;
      }

      warehouseTriggers.value = await customFormsService.getWarehouseTriggers(props.warehouse.id);
    };

    const getWarehouseLoadTypes = async () => {
      if (!props.warehouse?.id) {
        return [];
      }

      const response = await axios.get('loadtype', {
        params: {
          s: { warehouseId: props.warehouse.id }
        }
      });
      warehouseLoadTypes.value = response?.data?.data || [];
    };

    const getLoadTypeTriggers = async () => {
      if (!props.warehouse?.id || !warehouseLoadTypes.value?.length) {
        return [];
      }

      const triggers = await customFormsService.getLoadTypesTriggers(
        warehouseLoadTypes.value.map(l => l.id)
      );

      // Add the loadtype object to the trigger
      triggers.forEach(
        trigger =>
          (trigger.loadType = warehouseLoadTypes.value.find(l => l.id === trigger.objectId))
      );
      loadtypeTriggers.value = triggers;
    };

    const getAllTriggers = async () => {
      await getWarehouseLoadTypes();

      getWarehouseTriggers();
      getLoadTypeTriggers();
    };

    const handleSocketEvent = async event => {
      if (!event.warehouseId || event.warehouseId === props.warehouse.id) {
        await getAllTriggers();
      }
    };

    const featureData = feature => CustomFormsFeaturesData.value[feature] || {};

    const manageFeatureTrigger = trigger => {
      showAppointmentFields.value = false;
      selectedFeatureTriggerId.value = trigger.id;
    };

    const featureTriggers = feature =>
      warehouseTriggers.value.filter(trigger => trigger.feature === feature);

    onMounted(() => {
      getAllTriggers();

      Object.keys(sockets.actions).forEach(action => {
        eventHub.$on(`${action}-Trigger`, handleSocketEvent);
        eventHub.$on(`${action}-FormField`, handleSocketEvent);
        eventHub.$on(`${action}-Flow`, handleSocketEvent);
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(action => {
        eventHub.$off(`${action}-Trigger`);
        eventHub.$off(`${action}-FormField`);
        eventHub.$off(`${action}-Flow`);
      });
    });

    const handleFormCreated = async trigger => {
      await getAllTriggers();
      manageFeatureTrigger(trigger);
    };

    return {
      showAppointmentFields,
      handleFormCreated,
      selectedFeatureTriggerId,
      warehouseTriggers,
      loadtypeTriggers,
      warehouseLoadTypes,
      selectedFeatureTrigger,
      claimSupportFeatures,
      gateManagementFeatures,
      featureData,
      manageFeatureTrigger,
      featureTriggers,
      getAllTriggers,
      relatedTriggers,
      CustomFormsFeaturesData,
      CustomFormsFeatures
    };
  }
});
</script>
